/**
 * The Persian text pipeline (§5.3.3), mirroring App\Support\Persian\Normalizer byte-for-byte.
 * Both are tested against fixtures/persian-normalization.json, so the API, the storefront's
 * instant client filtering and the admin's slug preview cannot drift.
 */

const ZWNJ = '\u200C'

/** Arabic letters that become their Persian counterpart before matching (step 2). */
const LETTER_MAP: Record<string, string> = {
  '\u064A': '\u06CC', // ي -> ی
  '\u0649': '\u06CC', // ى -> ی
  '\u0643': '\u06A9', // ك -> ک
  '\u0629': '\u0647', // ة -> ه
  '\u06C0': '\u0647', // ۀ -> ه
  '\u0624': '\u0648', // ؤ -> و
  '\u0625': '\u0627', // إ -> ا
  '\u0623': '\u0627', // أ -> ا
  '\u0671': '\u0627', // ٱ -> ا
}

/** Punctuation that becomes a space when matching (step 6). */
const PUNCTUATION_MAP: Record<string, string> = {
  '\u060C': ' ', // ،
  '\u061B': ' ', // ؛
  '\u061F': ' ', // ؟
  '\u00AB': ' ', // «
  '\u00BB': ' ', // »
  '\u066A': '%', // ٪
  '\u0640': '', // tatweel
}

const DIACRITICS = /[\u064B-\u0652\u0670\u0640]/gu
const SLUG_SEPARATORS = /[^\p{L}\p{N}\u200C]+/gu

const PERSIAN_DIGITS = '۰۱۲۳۴۵۶۷۸۹'
const ARABIC_DIGITS = '٠١٢٣٤٥٦٧٨٩'

/** Persian (۰-۹) and Arabic-Indic (٠-٩) digits become ASCII (step 3). */
export function digits(value: string): string {
  return value.replace(/[۰-۹٠-٩]/gu, (d) => {
    const persian = PERSIAN_DIGITS.indexOf(d)
    if (persian !== -1) return String(persian)
    return String(ARABIC_DIGITS.indexOf(d))
  })
}

/** ASCII digits become Persian — presentation only, never for storage or matching. */
export function toPersianDigits(value: string): string {
  return value.replace(/[0-9]/g, (d) => PERSIAN_DIGITS[Number(d)] as string)
}

function mapChars(value: string, map: Record<string, string>): string {
  let out = ''
  for (const char of value) out += char in map ? map[char] : char
  return out
}

/** Steps 1-4 and 6-7: everything except the separator decision, which differs per use. */
function fold(value: string): string {
  let out = value.normalize('NFC')
  out = mapChars(out, LETTER_MAP)
  out = digits(out)
  out = out.replace(DIACRITICS, '')
  out = mapChars(out, PUNCTUATION_MAP)
  out = out.split(ZWNJ).join(' ')
  out = out.toLowerCase()
  return out.replace(/\s+/gu, ' ').trim()
}

/** Text as it is stored in the search index: fully folded, ZWNJ becomes a space. */
export function forIndex(value: string): string {
  return fold(value)
}

/** A user's query, folded exactly like the index so «كرم» finds «کرم». */
export function forQuery(value: string): string {
  return fold(value)
}

/**
 * URL slug: folding, then every separator — whitespace, ZWNJ and punctuation — collapses to `-`.
 * Never joins the halves of a ZWNJ compound: «مرطوب‌کننده» is `مرطوب-کننده`.
 */
export function slug(value: string, maxLength = 120): string {
  let out = value.normalize('NFC')
  out = mapChars(out, LETTER_MAP)
  out = digits(out)
  out = out.replace(DIACRITICS, '')
  out = mapChars(out, PUNCTUATION_MAP)
  out = out.toLowerCase()

  out = out.replace(SLUG_SEPARATORS, '-')
  out = out.split(ZWNJ).join('-')
  out = out.replace(/-+/gu, '-')
  out = out.replace(/^-+|-+$/gu, '')

  if ([...out].length > maxLength) {
    out = [...out].slice(0, maxLength).join('')
    const lastSeparator = out.lastIndexOf('-')
    if (lastSeparator > 0) out = out.slice(0, lastSeparator)
  }

  return out.replace(/^-+|-+$/gu, '')
}
